/**
 * StatCard — Dashboard 統計卡（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 升級亮點：
 *   - 英雄數字：Onest display，weight 700，36-48px，tabular-nums
 *   - 標籤：mono 字體 uppercase tracked，stone-500 弱化
 *   - 趨勢指示器：精緻 arrow，success/danger 色，semibold
 *   - icon chip：右上角圓形，tone 決定配色
 *   - 邊框：1px subtle，無預設陰影
 *   - hover：-1px translateY + shadow-md（僅可點擊時）
 *   - tone 決定 icon chip 配色，不染整個卡片
 */
import type { ReactNode } from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import {
  color,
  font,
  leading,
  radius,
  shadow,
  space,
  text,
  tracking,
  weight,
} from '@/styles/tokens';

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';

const TONE_MAP: Record<Tone, { fg: string; bg: string }> = {
  neutral: { fg: color.text.secondary, bg: color.surface.sunken },
  brand: { fg: color.brand.primary, bg: color.brand.primarySoft },
  success: { fg: color.state.success, bg: color.state.successSoft },
  warning: { fg: color.state.warning, bg: color.state.warningSoft },
  danger: { fg: color.state.danger, bg: color.state.dangerSoft },
  info: { fg: color.state.info, bg: color.state.infoSoft },
};

export interface StatCardProps {
  /** 卡片標題（弱化小標籤） */
  title: ReactNode;
  /** 主數值（英雄數字） */
  value: ReactNode;
  /** 數值單位（如「件」「個」「%」） */
  suffix?: ReactNode;
  /** 標題前綴文字（如「累計」） */
  prefix?: ReactNode;
  /** 右上角 icon chip */
  icon?: ReactNode;
  /** 趨勢值（如 +3 / -2） */
  delta?: ReactNode;
  /** 趨勢方向 */
  trend?: 'up' | 'down' | 'flat';
  /** 趨勢是否正面（上升=好 vs 下降=好）— 決定顏色是綠/紅 */
  trendPositive?: boolean;
  /** 趨勢說明（如「較上週」「較昨日」） */
  deltaLabel?: ReactNode;
  /** icon chip 色調 */
  tone?: Tone;
  /** 補充內容（如進度條、子數據） */
  footer?: ReactNode;
  /** 點擊事件（啟用則顯示 hover 微互動） */
  onClick?: () => void;
}

export default function StatCard({
  title,
  value,
  suffix,
  prefix,
  icon,
  delta,
  trend,
  trendPositive = true,
  deltaLabel,
  tone = 'brand',
  footer,
  onClick,
}: StatCardProps) {
  const palette = TONE_MAP[tone];

  /* 趨勢顏色：正面上升=綠，負面下降=紅，flat=灰 */
  const isPositiveTrend = trend === 'up' ? trendPositive : trend === 'down' ? !trendPositive : null;
  const trendColor =
    trend === 'flat' || isPositiveTrend === null
      ? color.text.tertiary
      : isPositiveTrend
        ? color.state.success
        : color.state.danger;

  const TrendIcon = trend === 'up' ? ArrowUpRight : trend === 'down' ? ArrowDownRight : Minus;

  return (
    <div
      onClick={onClick}
      style={{
        background: color.surface.base,
        border: `1px solid ${color.border.subtle}`,
        borderRadius: radius.md,
        padding: space[5],
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : undefined,
        transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      onMouseEnter={(e) => {
        if (!onClick) return;
        e.currentTarget.style.borderColor = color.border.default;
        e.currentTarget.style.boxShadow = shadow.md;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        if (!onClick) return;
        e.currentTarget.style.borderColor = color.border.subtle;
        e.currentTarget.style.boxShadow = shadow.xs;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      className="zhcb-stat-card"
    >
      {/* 標題行 + icon chip */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: space[3],
          gap: space[3],
        }}
      >
        {/* 標籤：mono uppercase tracked */}
        <span
          style={{
            fontFamily: font.mono,
            fontSize: text.xs,
            color: color.text.tertiary,
            letterSpacing: tracking.widest,
            fontWeight: weight.medium,
            textTransform: 'uppercase',
            lineHeight: leading.snug,
          }}
        >
          {title}
        </span>
        {icon && (
          <span
            style={{
              display: 'inline-flex',
              width: 34,
              height: 34,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: radius.md,
              background: palette.bg,
              color: palette.fg,
              flexShrink: 0,
            }}
          >
            {icon}
          </span>
        )}
      </div>

      {/* 英雄數字：Onest display，weight 700，tabular-nums */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        {prefix && (
          <span style={{ fontSize: text.md, color: color.text.muted, fontFamily: font.body }}>
            {prefix}
          </span>
        )}
        <span
          style={{
            fontFamily: font.display,
            fontSize: text['4xl'] /* 36px — 比 3xl 大一檔，更有視覺衝擊 */,
            fontWeight: weight.bold,
            color: color.text.primary,
            letterSpacing: tracking.tight,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: "'tnum' on",
          }}
        >
          {value}
        </span>
        {suffix && (
          <span
            style={{
              fontSize: text.lg,
              color: color.text.muted,
              marginLeft: 2,
              fontWeight: weight.medium,
            }}
          >
            {suffix}
          </span>
        )}
      </div>

      {/* 趨勢 / delta 行 */}
      {(delta !== undefined || deltaLabel) && (
        <div
          style={{
            marginTop: space[3],
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: text.sm,
          }}
        >
          {trend && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                color: trendColor,
                fontWeight: weight.semibold,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <TrendIcon size={13} strokeWidth={2.5} />
              {delta}
            </span>
          )}
          {!trend && delta !== undefined && (
            <span
              style={{
                color: color.text.secondary,
                fontWeight: weight.semibold,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {delta}
            </span>
          )}
          {deltaLabel && <span style={{ color: color.text.muted }}>{deltaLabel}</span>}
        </div>
      )}

      {/* footer（分隔線 + 補充信息） */}
      {footer && (
        <div
          style={{
            marginTop: space[4],
            paddingTop: space[3],
            borderTop: `1px solid ${color.border.subtle}`,
            fontSize: text.sm,
            color: color.text.tertiary,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
